import React, { useMemo } from "react";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { t } from "i18next";

import "./DownloadAppPrompt.scss";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
} from "../../utils/constants/generic-types";
import ImageHyperLogo from "../../images/hyper-text-icon-purple.svg";
import ImageMedalGamepad from "../../images/medal-gamepad.svg";
import ImagePlayWithGamers from "../../images/hyper-works-best-hero.png";
import GetHyperOnTestFlightModal from "../GetHyperOnTestFlightModal/GetHyperOnTestFlightModal";
import useDisclosure from "../../hooks/useDisclosure";
import DownloadAndroidAppModal from "../DownloadAndroidAppModal/DownloadAndroidAppModal";
import GetAppButton from "../GetAppButton/GetAppButton";
import useNotifications from "../../hooks/useNotifications";
import NotificationFlow from "../notification-flow/NotificationFlow";
import SecondaryActionButton from "../Buttons/SecondaryActionButton/SecondaryActionButton";
import useAuth from "../../hooks/useAuth";
import useGetApp from "../../hooks/useGetApp";
import { DownloadAppOptionProperty } from "../../utils/analytics";
import useAnalytics from "../../hooks/useAnalytics";
import { ROUTES } from "../../utils/constants";

enum LoggedInPromptDownloadScenarios {
  PLAY_WITH_FRIENDS = "logged-in-play-with-friends",
  PLAY_TOURNAMENT = "logged-in-play-tournament",
  PLAY_PENDING_GAMES = "logged-in-play-pending-games",
  CASHOUT = "logged-in-cashout",
  OTHERS = "logged-in-others",
}

enum LoggedOutPromptDownloadScenarios {
  CLICKS_FAVORITE_BUTTON = "clicks-favorite-button",
  SELECTS_FAVORITE_FILTER = "selects-favorite-filter",
  PLAY_WITH_FRIENDS = "play-with-friends",
  PLAY_TOURNAMENT = "play-tournament",
  OTHERS = "others",
}

export type PromptDownloadScenariosType =
  | LoggedInPromptDownloadScenarios
  | LoggedOutPromptDownloadScenarios;

export const PROMPT_DOWNLOAD_SCENARIOS = {
  LOGGED_IN: LoggedInPromptDownloadScenarios,
  LOGGED_OUT: LoggedOutPromptDownloadScenarios,
};

interface DownloadAppDialogProps extends GenericModalProps {
  onOpen: FunctionWithVoidReturn;
  showNotificationsFlow?: boolean;
  promptDownloadScenario?: PromptDownloadScenariosType;
}

export function useDialogMainText() {
  const { t } = useTranslation("common");

  const DIALOG_MAIN_TEXT: Record<PromptDownloadScenariosType, string> = useMemo(
    () => ({
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.CASHOUT]: t(
        "download_app_prompt.cash_out_in_two_minutes"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_PENDING_GAMES]: t(
        "download_app_prompt.play_your_pending_games"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_TOURNAMENT]: t(
        "download_app_prompt.play_multiplayer_tournaments"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_WITH_FRIENDS]: t(
        "download_app_prompt.challenge_your_real_life_friends"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS]: t(
        "download_app_prompt.get_the_full_experience"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.CLICKS_FAVORITE_BUTTON]: t(
        "download_app_prompt.add_games_you_enjoy_to_your_favorites"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.SELECTS_FAVORITE_FILTER]: t(
        "download_app_prompt.find_games_you_enjoy_faster"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_WITH_FRIENDS]: t(
        "download_app_prompt.challenge_your_friends"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_TOURNAMENT]: t(
        "download_app_prompt.play_to_win_rewards"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS]: t(
        "download_app_prompt.get_the_full_experience"
      ),
    }),
    [t]
  );

  return { DIALOG_MAIN_TEXT };
}

export function useDialogSubText() {
  const { ctaText } = useGetApp();
  const { t } = useTranslation("common");

  const DIALOG_SUB_TEXT: Record<PromptDownloadScenariosType, string> = useMemo(
    () => ({
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.CASHOUT]: t(
        "download_app_prompt.use_or_get_the_app_to_add_your_bank_account_and_get_your_rewards_immediately",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_PENDING_GAMES]: t(
        "download_app_prompt.replay_your_pending_games_to_win_the_reward_before_the_timer_runs_out"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_TOURNAMENT]: t(
        "download_app_prompt.use_or_get_the_app_to_play_with_many_gamers_and_get_bigger_prize_pools",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_WITH_FRIENDS]: t(
        "download_app_prompt.use_or_get_the_app_to_create_custom_game_links_anyone_can_challenge_you_on",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS]: t(
        "download_app_prompt.play_hundreds_zero_adds"
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.CLICKS_FAVORITE_BUTTON]: t(
        "download_app_prompt.log_in_or_use_the_app_to_add_games_to_your_favorite",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.SELECTS_FAVORITE_FILTER]: t(
        "download_app_prompt.use_or_get_the_app_to_find_your_favorite_games",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_WITH_FRIENDS]: t(
        "download_app_prompt.use_or_get_the_app_to_create_custom_game_links_to_share_with_friends",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_TOURNAMENT]: t(
        "download_app_prompt.use_or_get_the_app_to_enter_into_multiplayer_tournaments_and_win_rewards",
        { action: ctaText }
      ),
      [PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS]: t(
        "download_app_prompt.play_your_favorite_games_challenge_friends_and_win_rewards"
      ),
    }),
    [t, ctaText]
  );

  return { DIALOG_SUB_TEXT };
}

export default function DownloadAppPrompt({
  isOpen,
  onOpen,
  onClose,
  showNotificationsFlow = true,
  promptDownloadScenario = PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS,
}: DownloadAppDialogProps) {
  const {
    isOpen: isTestFlightInstructionsOpen,
    onOpen: openTestFlightInstructions,
    onClose: closeTestFlightInstructions,
  } = useDisclosure();
  const {
    isOpen: isAndroidAppDownloadOpen,
    onOpen: openDownloadAndroidApp,
    onClose: closeDownloadAndroidApp,
  } = useDisclosure();
  const { userIsInPwa, notificationsAreEnabled, ...notifications } =
    useNotifications();
  const { isLoggedIn: userIsLoggedIn } = useAuth();
  const { DIALOG_MAIN_TEXT } = useDialogMainText();
  const { DIALOG_SUB_TEXT } = useDialogSubText();
  const { sendDownloadAppOptionsEvent } = useAnalytics();
  const navigate = useNavigate();

  const handleDownloadApp = (openPlatformDialog: () => void) => {
    onClose();
    openPlatformDialog();
  };

  const handleRemindMeLater = () => {
    onClose();
    if (!notificationsAreEnabled) {
      notifications.openEnableNotificationsDialog();
    }
  };

  const handleSecondaryAction = () => {
    if (userIsLoggedIn) {
      sendDownloadAppOptionsEvent(DownloadAppOptionProperty.SKIP);
      handleRemindMeLater();
      return;
    }
    sendDownloadAppOptionsEvent(DownloadAppOptionProperty.LOG_IN);
    navigate(ROUTES.ROOT.LOG_IN);
  };

  const handleClosePrompt = () => {
    sendDownloadAppOptionsEvent(DownloadAppOptionProperty.SKIP);
    if (userIsLoggedIn) {
      handleRemindMeLater();
      return;
    }
    onClose();
  };

  return (
    <>
      <BottomDrawer isOpen={isOpen} onClose={handleClosePrompt}>
        <Stack gap={4} className="align-items-center DownloadAppDialog">
          {!userIsLoggedIn && (
            <Image
              src={ImageHyperLogo}
              alt="Hyper logo"
              width={119}
              height={20}
            />
          )}
          <Stack className="align-items-center TextWithGamepad">
            {!userIsLoggedIn && (
              <Image
                src={ImageMedalGamepad}
                alt="Gamepad image"
                width={123}
                height={141.6}
              />
            )}
            {userIsLoggedIn && (
              <Image
                src={ImagePlayWithGamers}
                alt="Gamepad image"
                width={221}
                height={272}
                className="mt-3"
              />
            )}
            <Stack gap={2} className="DialogText">
              <p className="fs-6 Hanson-700 SpecialFontSettings MainText">
                {DIALOG_MAIN_TEXT[promptDownloadScenario]}
              </p>
              <p className="Grotesk SpecialFontSettings SubText">
                {DIALOG_SUB_TEXT[promptDownloadScenario]}
              </p>
            </Stack>
          </Stack>
          <Stack className="align-items-center Ctas">
            <GetAppButton
              openDownloadAndroidApp={() =>
                handleDownloadApp(openDownloadAndroidApp)
              }
              openTestFlightInstructions={() =>
                handleDownloadApp(openTestFlightInstructions)
              }
            />
            <SecondaryActionButton
              text={
                userIsLoggedIn
                  ? t("download_app_prompt.remind_me_later")
                  : t("download_app_prompt.log_into_hyper")
              }
              onClick={handleSecondaryAction}
            />
          </Stack>
        </Stack>
      </BottomDrawer>

      <GetHyperOnTestFlightModal
        isOpen={isTestFlightInstructionsOpen}
        onClose={closeTestFlightInstructions}
      />
      <DownloadAndroidAppModal
        isOpen={isAndroidAppDownloadOpen}
        onClose={closeDownloadAndroidApp}
      />
      {showNotificationsFlow && (
        <NotificationFlow handleGetApp={onOpen} {...notifications} />
      )}
    </>
  );
}
